import { createSelector } from '@reduxjs/toolkit';
import { TitleEntity, VersionGroupedByParent } from '../slices/slice_types';
import { LoadingStatuses } from '@/constants/constants';
import { set } from 'lodash';
import { RootState } from '../store';
import { TitleEntityV2 } from '../slices/titleV2Slice.types';

const selectOrderPlacementState = (state: RootState) => state.orderPlacement;

export const selectFilters = createSelector(selectOrderPlacementState, (orderPlacementState) => orderPlacementState?.filters || {});

export const selectOpenCancelationDialog = createSelector(
  selectOrderPlacementState,
  (orderPlacementState) => orderPlacementState?.openCancelationDialog || false
);

export const selectFormIsIncomplete = createSelector(
  selectOrderPlacementState,
  (orderPlacementState) => orderPlacementState?.formIsIncomplete || false
);

export const selectFormIsDraft = createSelector(selectOrderPlacementState, (orderPlacementState) => orderPlacementState?.formIsDraft || false);

export const selectDraftState = createSelector(selectOrderPlacementState, (orderPlacementState) => orderPlacementState?.draftState || {});

export const selectIsResumedDraft = createSelector(selectOrderPlacementState, (orderPlacementState) => orderPlacementState?.isResumedDraft || false);

export const selectSelectedVersionId = createSelector(
  selectOrderPlacementState,
  (orderPlacementState) => orderPlacementState?.selectedVersionId || null
);

export const selectInventory = createSelector(selectOrderPlacementState, (slice) => slice?.data || []);

export const selectInventoryIsLoading = createSelector(selectOrderPlacementState, (slice) => slice.status === LoadingStatuses.pending || false);

export const selectInventoryStatus = createSelector(selectOrderPlacementState, (slice) => slice?.status);
export const selectHasInventory = createSelector(selectInventory, (inventory) => !!inventory.length || false);

export const selectTitle = createSelector(selectOrderPlacementState, (slice) => slice.data || {});
export const selectStep = createSelector(selectOrderPlacementState, (inventory) => inventory.step || 0);

export const selectSearchValue = createSelector(selectOrderPlacementState, (slice) => slice.searchValue);
export const selectTitleSearchResults = createSelector(selectOrderPlacementState, (slice) => slice.searchResults?.data || []);
export const selectTitleSearchIsLoading = createSelector(selectOrderPlacementState, (slice) => slice.searchResults.loading);

export const selectInventorySelections = createSelector(selectOrderPlacementState, (slice) => slice.inventorySelections || []);

export const selectVersionSelections = createSelector(selectOrderPlacementState, (slice) => slice.versionSelections || []);

export const selectOutputVersions = createSelector(selectOrderPlacementState, (slice) => slice.outputVersions || []);

export const selectOutputVersionOptions = createSelector(selectOrderPlacementState, (slice) => slice.outputVersionOptions || {});
export const selectSquashedOutputVersionOptions = createSelector(selectOrderPlacementState, (slice) => slice.squashedOutputVersions || {});

export const selectVersionKeys = createSelector(selectOrderPlacementState, (slice) => slice.versionKeys || {});

export const selectProfileData = createSelector(selectOrderPlacementState, (slice) => slice.profileData || {});

export const selectPrimaryVersionIds = createSelector(selectOrderPlacementState, (slice) => slice.primaryVersionIds || []);

export const selectNewOrderId = createSelector(selectOrderPlacementState, (slice) => slice.newOrderId || null);

export const selectPreviewFile = createSelector(selectOrderPlacementState, (slice) => slice.previewFile);
export const selectTitleInput = createSelector(selectOrderPlacementState, (slice) => slice.titleInput);

export const selectVersions = createSelector(selectOrderPlacementState, (slice) => slice.versionSelections || []);

export const selectValidation = createSelector(selectOrderPlacementState, (slice) => slice.validationErrors || {});

export const selectGroupedVersionSelections = createSelector([selectVersionSelections], (selectedVersions): VersionGroupedByParent => {
  if (selectedVersions.length === 0) {
    return {};
  }

  const groupedVersions: VersionGroupedByParent = {};
  const data: TitleEntity[] = JSON.parse(JSON.stringify(selectedVersions));

  // for each version we have, created nested objects down to the version
  data.forEach((selectedVersion: TitleEntity) => {
    if (!selectedVersion.lineage || selectedVersion.lineage.length === 0) {
      // if there's no lineage, just add it to the root
      groupedVersions[`_${selectedVersion.id}`] = selectedVersion;
      return;
    }
    let lineage = [...selectedVersion.lineage].reverse();
    // add an underscore so lodash can recognize it as a key instead of an index
    lineage = lineage.map((id) => `_${id}`);
    // add the version id to the end of the lineage
    lineage.push(`_${selectedVersion.id}`);
    set(groupedVersions, lineage, selectedVersion);
  });

  return groupedVersions;
});

export const selectSchemaFormRefs = createSelector(selectOrderPlacementState, (slice) => slice.schemaFormRefs || new Map());
export const selectSelectedHierarchy = createSelector(selectOrderPlacementState, (slice) => slice.selectedHierarchy || '');
export const selectToggledTitleSearchMode = createSelector(selectOrderPlacementState, (slice) => slice.toggledTitleSearchMode || 'default');

const toLookupId = (groupedKey: string): string => groupedKey.replace(/^_/, '');

const countSelectedEpisodes = (node: VersionGroupedByParent): number => {
  let count = 0;

  const countEpisodes = (value: any) => {
    if (!value) return;

    if (value?.entity_type === 'version') {
      count += 1;
      return;
    }

    if (typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        countEpisodes(value[key]);
      });
    }
  };

  countEpisodes(node);

  return count;
};

const findVersionPath = (node: any, versionId: number | string, path: string[] = []): string[] | null => {
  if (!node || typeof node !== 'object') return null;

  const targetKey = `_${versionId}`;

  for (const key of Object.keys(node)) {
    const value = node[key];

    if (key === targetKey) {
      return [...path, key];
    }

    if (typeof value === 'object') {
      const result = findVersionPath(value, versionId, [...path, key]);
      if (result) return result;
    }
  }

  return null;
};

export type VersionHierarchyEntities = {
  seriesEntity: TitleEntity | TitleEntityV2 | null;
  seasonEntity: TitleEntity | TitleEntityV2 | null;
  episodeCount: number;
};

export const makeSelectVersionHierarchyEntities = () =>
  createSelector(
    [selectGroupedVersionSelections, selectVersionKeys, (_: RootState, versionId: number | string) => versionId],
    (groupedByParent, versionsByKey, versionId): VersionHierarchyEntities => {
      const path = findVersionPath(groupedByParent, versionId);

      if (!path || path.length === 0) {
        return {
          seriesEntity: null,
          seasonEntity: null,
          episodeCount: 0
        };
      }

      const seriesKey = path[0];
      const seasonKey = path.length > 2 ? path[1] : null;

      const seriesEntity = seriesKey ? versionsByKey[toLookupId(seriesKey)] || null : null;
      const seasonEntity = seasonKey ? versionsByKey[toLookupId(seasonKey)] || null : null;

      let episodeCount = 1;

      if (seasonKey && groupedByParent?.[seriesKey]?.[seasonKey]) {
        episodeCount = countSelectedEpisodes(groupedByParent[seriesKey][seasonKey]);
      } else if (seriesKey && groupedByParent?.[seriesKey]) {
        episodeCount = countSelectedEpisodes(groupedByParent[seriesKey]);
      }

      return {
        seriesEntity,
        seasonEntity,
        episodeCount
      };
    }
  );
